import { useTranslation } from 'react-i18next';
import { ExtraWorkOrderLine, ExtraWorkOrder } from '../../../List/models';
import { Product } from '@shared/components/ui/Product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faClock,
  faMapLocationDot,
  faTag,
  faCircleMinus,
  faChevronDown,
  faChevronUp,
  faFolder,
} from '@fortawesome/pro-light-svg-icons';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useUsers } from '../../hooks/useUsers';
import { getExtraWorkOrderStatusConfig } from '../../../List/config/extraWorkOrderStatusConfig';
import { format } from 'date-fns';
import { TooltipProvider, TooltipSH, TooltipTrigger, TooltipContent } from '@shared/components/ui/shadcn/tooltip';
import { EvidenceImageModal } from './EvidenceImageModal';

interface GroupDetailProps {
  line: ExtraWorkOrderLine;
  order?: ExtraWorkOrder | null;
}

/**
 * Detalle de grupo - Muestra la información completa de un grupo escaneado
 *
 * Se muestra cuando se ha escaneado y validado un grupo específico
 */
export const GroupDetail = ({ line }: GroupDetailProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const [isWastesExpanded, setIsWastesExpanded] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState<{ url: string; fileName: string } | null>(null);
  const { getUserName } = useUsers();

  // Log para debugging - verificar que la línea se actualiza con wastes
  console.log('🔍 GroupDetail - Línea recibida:', {
    id: line.id,
    group_code: line.group_code,
    qty_done: line.qty_done,
    qty_waste: line.qty_waste,
    wastesCount: line.wastes?.length || 0,
    wastes: line.wastes,
  });

  // 🎯 Obtener producto
  const productIds = useMemo(() => {
    if (line.product_id > 0) return [line.product_id];
    return [];
  }, [line.product_id]);

  // 🎯 Consultar producto desde la API
  const { data: productsData } = useQuery({
    queryKey: ['product-for-group', productIds],
    queryFn: async () => {
      if (productIds.length === 0) return [];

      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.get<any>('/products', {
        fields: 'basic',
        product_ids: productIds.join(','),
        per_page: 1,
      });

      return response.data || [];
    },
    enabled: productIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // 🎯 Obtener producto
  const product = useMemo(() => {
    if (!productsData || productsData.length === 0) return line.product;
    return productsData[0];
  }, [productsData, line.product]);

  // 🎯 Calcular progreso
  const progress = {
    done: line.qty_done,
    total: line.qty_expected,
  };
  const isComplete = line.status === 'DONE';
  const productNotFound = !product;
  const statusConfig = getExtraWorkOrderStatusConfig(line.status);
  const hasWastes = line.wastes && line.wastes.length > 0;

  // Formatear fechas
  const formattedCreatedAt = line.created_at ? format(new Date(line.created_at), 'dd/MM/yyyy HH:mm') : null;
  const formattedStartedAt = line.started_at ? format(new Date(line.started_at), 'dd/MM/yyyy HH:mm') : null;

  // Calcular porcentaje de progreso
  const progressPercentage = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-title">{t('detail.group.title', 'Grupo Escaneado')}</h3>
      </div>

      {/* Card del grupo escaneado - Mismo estilo que Carrito Activo (bg y borde) */}
      <div
        onClick={() => {
          // Si hay mermas, toggle al hacer click en cualquier parte del bloque
          if (hasWastes) {
            setIsWastesExpanded(!isWastesExpanded);
          }
        }}
        className={`rounded-lg border border-border overflow-hidden transition-colors bg-green-50 dark:bg-green-950/20 ${
          hasWastes ? 'hover:opacity-95 cursor-pointer' : ''
        }`}
      >
        {/* Barra de progreso - Contra el borde superior */}
        <div className="w-full h-1 relative">
          <div
            className={`h-full ${isComplete ? 'bg-success' : 'bg-info'}`}
            style={{ width: isComplete ? '100%' : `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>

        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Columna izquierda: Nombre del grupo y Product */}
            <div className="flex-1 min-w-0">
              {/* Nombre del grupo y Estado - Arriba (icono tipo Carrito Activo) */}
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-green-100 dark:bg-green-900/40 rounded-lg p-2 flex-shrink-0">
                  <FontAwesomeIcon icon={faCircleCheck} className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-base font-bold text-text">{line.group_code}</p>
                {/* Badge de estado */}
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.text}`}
                >
                  {t(`lineStatus.${line.status}`, statusConfig.label)}
                </span>
              </div>

              {/* Línea separadora entre título y producto */}
              <div className="border-t border-lines my-3" />

              {/* Componente Product - Fuente más grande y cantidades en negrita */}
              <div className="mb-3 [&_.text-sm]:text-base [&_.text-xs]:text-sm [&_.font-normal]:font-bold">
                <Product
                  id={product?.id || line.product_id}
                  src={line.product?.image_hash || product?.image_hash || product?.image || ''}
                  name={product?.name || t('detail.lines.product_not_found', 'Producto no encontrado')}
                  code={productNotFound ? String(line.product_id) : product?.code || ''}
                  acronym={productNotFound ? undefined : product ? undefined : 'PN'}
                  enableLineClamp={false}
                  showTooltip={false}
                />
              </div>

              {/* Datos adicionales con iconos - Debajo del producto */}
              <TooltipProvider>
                <div className="flex flex-wrap items-center gap-4 text-sm text-complementary-text">
                  {/* Fecha de creación o inicio - Primer item */}
                  {(formattedCreatedAt || formattedStartedAt) && (
                    <TooltipSH>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 cursor-help">
                          <FontAwesomeIcon icon={faClock} className="w-3.5 h-3.5" />
                          <span>{formattedStartedAt || formattedCreatedAt}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-body-bg border-card-border">
                        <p>
                          {formattedStartedAt
                            ? t('detail.header.started_at', 'Iniciada')
                            : t('detail.lines.created_at', 'Fecha creación')}
                        </p>
                      </TooltipContent>
                    </TooltipSH>
                  )}

                  {/* Tipo de trabajo */}
                  <TooltipSH>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-help">
                        <FontAwesomeIcon icon={faTag} className="w-3.5 h-3.5" />
                        <span>{t(`workTypes.${line.work_type}`, line.work_type)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-body-bg border-card-border">
                      <p>{t('detail.lines.work_type', 'Tipo trabajo')}</p>
                    </TooltipContent>
                  </TooltipSH>

                  {/* Ubicación destino */}
                  {line.done_location && (
                    <TooltipSH>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 cursor-help">
                          <FontAwesomeIcon icon={faMapLocationDot} className="w-3.5 h-3.5" />
                          <span>{line.done_location.code || line.done_location.name || '-'}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-body-bg border-card-border">
                        <p>{t('detail.lines.done_location', 'Ubicación destino')}</p>
                      </TooltipContent>
                    </TooltipSH>
                  )}

                  {/* Cantidad merma - Último item con fondo warning */}
                  {line.qty_waste > 0 && (
                    <TooltipSH>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 cursor-help bg-bg-warning text-text px-2 py-0.5 rounded">
                          <FontAwesomeIcon icon={faCircleMinus} className="w-3.5 h-3.5" />
                          <span>{line.qty_waste}</span>
                          {hasWastes && (
                            <FontAwesomeIcon
                              icon={isWastesExpanded ? faChevronUp : faChevronDown}
                              className="w-2.5 h-2.5"
                            />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-body-bg border-card-border">
                        <p>{t('detail.lines.qty_waste', 'Cantidad merma')}</p>
                      </TooltipContent>
                    </TooltipSH>
                  )}
                </div>
              </TooltipProvider>
            </div>

            {/* Estado de progreso y flecha - A la derecha */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {isComplete && <FontAwesomeIcon icon={faCircleCheck} className="w-5 h-5 text-success" />}
              <span className={`text-base font-bold ${isComplete ? 'text-success' : 'text-text'}`}>
                {progress.done}/{progress.total}
              </span>
            </div>
          </div>
        </div>

        {/* Sección de Mermas desplegable */}
        {hasWastes && isWastesExpanded && (
          <div className="px-4 pb-4">
            <div className="space-y-3">
              {line.wastes!.map((waste, index) => (
                <div key={waste.id} className="bg-card-bg border border-card-border rounded-lg p-4">
                  {/* Título: Merma número */}
                  <div className="mb-3">
                    <h5 className="text-base font-semibold mb-2 text-title">
                      {t('detail.wastes.title_singular', 'Merma')} {index + 1}
                    </h5>
                    <div className="border-t border-lines"></div>
                  </div>

                  {/* Información: Cantidad • Fecha • Registrado por */}
                  <div className="text-sm mb-3 text-text">
                    <span>
                      <span className="text-xs text-complementary-text">{t('detail.wastes.qty', 'Cantidad')}</span>{' '}
                      <span className="font-medium">{waste.qty}</span>
                    </span>
                    {waste.created_at && (
                      <>
                        <span className="mx-2 text-complementary-text">•</span>
                        <span>
                          <span className="text-xs text-complementary-text">
                            {t('detail.wastes.created_at', 'Fecha')}
                          </span>{' '}
                          <span>{format(new Date(waste.created_at), 'dd/MM/yyyy HH:mm')}</span>
                        </span>
                      </>
                    )}
                    <span className="mx-2 text-complementary-text">•</span>
                    <span>
                      <span className="text-xs text-complementary-text">
                        {t('detail.wastes.created_by', 'Registrado por')}
                      </span>{' '}
                      <span>{getUserName(waste.created_by)}</span>
                    </span>
                  </div>

                  {/* Evidencias - Cada una clickeable */}
                  {waste.evidences && waste.evidences.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <FontAwesomeIcon icon={faFolder} className="w-3 h-3 text-icons" />
                        {waste.evidences.map((evidence: any, idx: number) => {
                          const filePath =
                            typeof evidence === 'string' ? evidence : evidence.file_path || evidence.file_name || '';
                          const fileName =
                            typeof evidence === 'string'
                              ? evidence.split('/').pop() || `evidencia_${idx + 1}`
                              : evidence.file_name || evidence.file_path?.split('/').pop() || `evidencia_${idx + 1}`;
                          return (
                            <button
                              key={idx}
                              type="button"
                              className="text-sm text-info hover:underline cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEvidence({ url: filePath, fileName });
                              }}
                              data-testid={`evidence-link-${waste.id}-${idx}`}
                            >
                              {fileName}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Separador */}
                  {waste.description && <div className="border-t border-lines mb-3"></div>}

                  {/* Descripción */}
                  {waste.description && (
                    <div>
                      <p className="text-sm text-text">{waste.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal de evidencia */}
      <EvidenceImageModal
        isOpen={!!selectedEvidence}
        onClose={() => setSelectedEvidence(null)}
        imageUrl={selectedEvidence?.url || ''}
        fileName={selectedEvidence?.fileName}
        data-testid="evidence-image-modal"
      />
    </div>
  );
};
